'use client'

import { useEffect, useState } from 'react'
import { ArrowLeft, ExternalLink, Mail } from 'lucide-react'

import type { Locale } from '@helper/utils/types'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { buildUrl } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useAuthClient } from '../context'
import { ChangePasswordForm } from '../forms/change-password'
import { ActiveDevicesList } from './active-devices-list'

/** Placeholder shown in the read-only password field — the real password is never sent to the client. */
const PASSWORD_MASK = '•'.repeat(16)

/**
 * Footer that sits flush against the bottom of the dialog, mirroring `DialogFooter`.
 * `ChangePasswordForm` renders its own submit button, so its footer row gets these
 * classes instead of being wrapped in `DialogFooter`.
 */
const DIALOG_FOOTER_CLASS =
  'srd:bg-grey-10/50 srd:border-outline srd:-mx-6 srd:-mb-6 srd:rounded-b-[28px] srd:border-t srd:p-4'

type SecurityView = 'overview' | 'password'

interface AccountSecurityDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Email shown in the read-only email field. */
  email?: string | null
  locale?: Locale
  className?: string
}

/**
 * "Keamanan akun" dialog: read-only email and password with links to change them,
 * plus the list of devices currently signed in.
 *
 * Changing the email hands off to the account app, which owns the verification flow.
 * Changing the password swaps this dialog into a password sub-view.
 */
export function AccountSecurityDialog({
  open,
  onOpenChange,
  email,
  locale = 'id',
  className,
}: AccountSecurityDialogProps) {
  const authClient = useAuthClient()
  const [view, setView] = useState<SecurityView>('overview')

  // Always land on the overview when the dialog is opened again.
  useEffect(() => {
    if (open) setView('overview')
  }, [open])

  const linkClassName = cn(
    typographyVariants({ variant: 'label-sm' }),
    'srd:text-blue-100 srd:h-auto srd:w-fit srd:self-end srd:p-0',
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('srd:max-h-[90dvh] srd:w-full srd:max-w-lg', className)}>
        {view === 'overview' ? (
          <>
            <DialogHeader>
              <DialogTitle>{locale === 'id' ? 'Keamanan akun' : 'Account security'}</DialogTitle>
              <DialogDescription>
                {locale === 'id' ? 'Pengaturan keamanan akun' : 'Account security settings'}
              </DialogDescription>
            </DialogHeader>

            <div className='srd:mt-4 srd:flex srd:flex-col srd:gap-6'>
              <div className='srd:flex srd:flex-col srd:gap-4'>
                <Field className='srd:gap-1'>
                  <FieldLabel htmlFor='account-security-email'>Email</FieldLabel>
                  <InputGroup className='srd:has-disabled:opacity-100 ui:group-data-[disabled=true]/input-group:opacity-100'>
                    <InputGroupAddon align='inline-start'>
                      <Mail className='srd:text-blue-100' />
                    </InputGroupAddon>
                    <InputGroupInput
                      id='account-security-email'
                      name='email'
                      value={email ?? '—'}
                      readOnly
                      disabled
                      className='ui:disabled:opacity-100'
                    />
                  </InputGroup>

                  <Button asChild variant='link' size='sm' className={linkClassName}>
                    <a href={buildUrl('account', '/ganti-email').href} target='_blank' rel='noreferrer'>
                      {locale === 'id' ? 'Ganti email' : 'Change email'}
                      <ExternalLink className='srd:ml-1 srd:inline-block srd:size-3' />
                    </a>
                  </Button>
                </Field>

                <Field className='srd:gap-1'>
                  <FieldLabel htmlFor='account-security-password'>Password</FieldLabel>
                  <Input
                    id='account-security-password'
                    name='password'
                    value={PASSWORD_MASK}
                    readOnly
                    disabled
                    aria-label={locale === 'id' ? 'Password saat ini' : 'Current password'}
                    className='ui:disabled:opacity-100'
                  />

                  <Button
                    type='button'
                    variant='link'
                    size='sm'
                    className={linkClassName}
                    onClick={() => setView('password')}
                  >
                    {locale === 'id' ? 'Ubah password' : 'Change password'}
                  </Button>
                </Field>
              </div>

              <div className='srd:flex srd:flex-col srd:gap-3'>
                <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-grey-100')}>
                  {locale === 'id' ? 'Perangkat aktif' : 'Active devices'}
                </p>

                <ActiveDevicesList locale={locale} />
              </div>
            </div>

            <DialogFooter className='srd:mt-2'>
              <Button variant='outline' onClick={() => onOpenChange(false)}>
                <ArrowLeft className='srd:mr-2 srd:size-4' />
                {locale === 'id' ? 'Kembali' : 'Back'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{locale === 'id' ? 'Ubah password' : 'Change password'}</DialogTitle>
              <DialogDescription>
                {locale === 'id'
                  ? 'Lakukan pergantian password untuk keamanan akunmu'
                  : 'Change your password to keep your account secure'}
              </DialogDescription>
            </DialogHeader>

            <ChangePasswordForm
              locale={locale}
              showHeader={false}
              className='srd:mt-4'
              submitButtonText={locale === 'id' ? 'Simpan' : 'Save'}
              footerClassName={DIALOG_FOOTER_CLASS}
              footerActions={
                <Button type='button' variant='outline' onClick={() => setView('overview')}>
                  <ArrowLeft className='srd:mr-2 srd:size-4' />
                  {locale === 'id' ? 'Kembali' : 'Back'}
                </Button>
              }
              onSubmit={async (values) => {
                const { error } = await authClient.changePassword({
                  currentPassword: values.oldPassword,
                  newPassword: values.newPassword,
                })

                if (error) {
                  throw error as unknown as Error
                }

                toast.success(locale === 'id' ? 'Password berhasil diubah' : 'Password updated successfully')
                setView('overview')
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
